import React, {useCallback, useEffect, useState} from "react";
import {ApiError, formatApiError, getDepositCase, listAppeals} from "../../api";
import type {AppealItem} from "../../api";
import {useAuth} from "../../auth";
import {ErrorBanner} from "../../components";
import {navigate, useRoute} from "../../hooks";
import {formatAmount, formatDate} from "../../utils";
import {DisputeDetails} from "./DisputeDetails";

// Подписи источников спора; неизвестный код остаётся видимым.
const APPEAL_REASON_LABELS: Record<string, string> = {
    trader: "трейдер",
    claim_timeout: "вышел срок брони",
    external_source: "заявка площадки закрыта",
    exchange_deposit_timeout: "истёк срок поступления USDT",
    exchange_deposit_review: "спор по переводу USDT",
};

function appealReasonLabel(reason: string): string {
    return APPEAL_REASON_LABELS[reason] ?? reason;
}

export function AdminDisputesPage(): JSX.Element {
    const {token} = useAuth();
    const route = useRoute();
    const query = new URLSearchParams(route.split("?")[1] ?? "");
    const routeOrderId = query.get("orderId");
    const routeCaseId = query.get("caseId");

    const [items, setItems] = useState<AppealItem[]>([]);
    const [selected, setSelected] = useState<AppealItem | null>(null);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const [success, setSuccess] = useState("");
    const [routeNotice, setRouteNotice] = useState("");

    const reload = useCallback((): void => {
        if (!token) return;
        setLoading(true);
        setError(null);
        listAppeals(token)
            .then((rows) => {
                setItems(rows);
                setLoaded(true);
                // Строка списка опознаётся по ЗАЯВКЕ, а не по платежу: платёж у
                // спорной заявки меняется (вернули в пул, взял другой трейдер),
                // а бывает, что его нет совсем. По paymentId такая строка
                // потерялась бы на первом же обновлении.
                setSelected((current) => {
                    if (!current) return null;
                    return rows.find((item) => item.orderId === current.orderId) ?? null;
                });
            })
            .catch((err) => setError(err))
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        if (!token || !routeCaseId || routeOrderId) return;
        let cancelled = false;
        void getDepositCase(token, routeCaseId).then((depositCase) => {
            if (cancelled) return;
            if (!depositCase.orderId) {
                setRouteNotice("Дело USDT не привязано к заявке и не может быть открыто в деталях спора.");
                navigate("#/admin/disputes");
                return;
            }
            navigate(`#/admin/disputes?orderId=${encodeURIComponent(depositCase.orderId)}&caseId=${encodeURIComponent(routeCaseId)}`);
        }).catch((err) => { if (!cancelled) setRouteNotice(formatApiError(err)); });
        return () => { cancelled = true; };
    }, [routeCaseId, routeOrderId, token]);

    useEffect(() => {
        if (loading || !loaded) return;
        if (!routeOrderId) {
            if (selected) setSelected(null);
            return;
        }
        const next = items.find((item) => item.orderId === routeOrderId);
        if (next) {
            setRouteNotice("");
            if (!selected || selected.orderId !== routeOrderId) setSelected(next);
            return;
        }
        setSelected(null);
        setRouteNotice("Заявка со спором не найдена в текущем списке.");
    }, [items, loaded, loading, routeOrderId, selected]);

    useEffect(() => {
        if (selected) return;
        const refreshVisible = (): void => { if (document.visibilityState !== "hidden") reload(); };
        const timer = window.setInterval(refreshVisible, 5000);
        document.addEventListener("visibilitychange", refreshVisible);
        return () => { window.clearInterval(timer); document.removeEventListener("visibilitychange", refreshVisible); };
    }, [reload, selected]);

    // Ключ — заявка, а не платёж: у разобранной заявки без держателя платежа
    // нет вовсе, а фильтр по `paymentId !== null` выкинул бы из списка все
    // остальные строки без платежа заодно.
    const handleResolved = (orderId: string, merchantStatus: string): void => {
        setItems((prev) => prev.filter((item) => item.orderId !== orderId));
        setSelected(null);
        setError(null);
        setSuccess(`Спор разрешён. Мерчанту отправлен статус ${merchantStatus}.`);
        navigate("#/admin/disputes");
        reload();
    };

    const handleConflict = (err: unknown): void => {
        setSuccess("");
        setError(err);
        reload();
    };

    if (selected) {
        return (
            <DisputeDetails
                key={selected.orderId}
                item={selected}
                initialCaseId={routeCaseId}
                onBack={() => { setSelected(null); navigate("#/admin/disputes"); }}
                onResolved={handleResolved}
                onConflict={handleConflict}
                onReviewChanged={reload}
            />
        );
    }

    return (
        <div className="page admin-disputes">
            <div className="page-header">
                <h2>Споры</h2>
                <button className="btn btn-ghost btn-sm" type="button" onClick={reload} disabled={loading}>
                    {loading ? "Обновление..." : "Обновить"}
                </button>
            </div>

            <ErrorBanner error={routeNotice ? new Error(routeNotice) : null} title="Не удалось открыть спор" />

            {success && (
                <div className="alert alert-info">
                    {success}{" "}
                    <button className="alert-dismiss" type="button" onClick={() => setSuccess("")}>
                        ×
                    </button>
                </div>
            )}

            <ErrorBanner
                error={error}
                title={error instanceof ApiError && error.status === 409 ? "Список обновлён после конфликта" : "Не удалось загрузить споры"}
            />

            {loading && items.length === 0 ? (
                <div className="empty-state">Загрузка споров...</div>
            ) : items.length === 0 ? (
                <div className="empty-state">Споров нет</div>
            ) : (
                <div className="table-wrapper disputes-table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th>Мерчант</th>
                                <th>Трейдер</th>
                                <th>remoteId</th>
                                <th>Сумма</th>
                                <th>Остаток</th>
                                <th>Открыт спор</th>
                                <th>Кем</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item) => (
                                // Ключ — заявка: платёж у спорной заявки бывает
                                // разный и бывает никакой, а заявка одна.
                                <tr
                                    key={item.orderId}
                                    className={`dispute-row ${item.paymentId === null ? "dispute-row-unheld" : ""}`}
                                    onClick={() => { setSelected(item); navigate(`#/admin/disputes?orderId=${encodeURIComponent(item.orderId)}`); }}
                                >
                                    <td>
                                        <strong>{item.merchantName}</strong>
                                    </td>
                                    {/* Заявка со спором лежит в пуле без платежа —
                                        держателя у неё сейчас нет, и придумывать
                                        его нечем. Прочерк честнее пустой ячейки:
                                        пустая читается как «не загрузилось». */}
                                    <td>
                                        {item.traderName ?? <span className="muted">в пуле, держателя нет</span>}
                                    </td>
                                    <td className="mono dispute-remote-cell">{item.remoteId}</td>
                                    <td className="mono">{formatAmount(item.amount)} ₽</td>
                                    <td>
                                        <span className="mono">{formatAmount(item.amountRemaining)} ₽</span>
                                        <span className="dispute-parts">
                                            {item.paymentsMade}/{item.maxPayments}
                                        </span>
                                    </td>
                                    <td>{formatDate(item.appealedAt)}</td>
                                    <td>{appealReasonLabel(item.appealReason)}</td>
                                    <td className="merchant-action-cell">
                                        <button className="btn btn-ghost btn-sm" type="button">
                                            Открыть →
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
